<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">
              <i class="bi bi-play-circle me-2"></i>
              Ejecución de Scripts
            </h1>
            <p class="text-muted mb-0">
              Administra scripts de monitoreo en segundo plano
            </p>
          </div>
          <router-link
            to="/scripts/create"
            class="btn btn-primary"
          >
            <i class="bi bi-plus-lg me-1"></i>
            Nuevo Script
          </router-link>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="bi bi-funnel me-1"></i>
          Filtros
        </h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="applyFilters" class="row g-3">
          <div class="col-md-6">
            <label for="filterClient" class="form-label">Cliente</label>
            <select
              id="filterClient"
              v-model="localFilters.client_id"
              class="form-select"
            >
              <option :value="null">Todos los clientes</option>
              <option v-for="client in clients" :key="client.id" :value="client.id">
                {{ client.nombre }}
              </option>
            </select>
          </div>
          <div class="col-md-6">
            <label for="filterStatus" class="form-label">Estado</label>
            <select
              id="filterStatus"
              v-model="localFilters.status"
              class="form-select"
            >
              <option :value="null">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="running">En Ejecución</option>
              <option value="stopped">Detenido</option>
              <option value="failed">Fallido</option>
              <option value="completed">Completado</option>
            </select>
          </div>
          <div class="col-12">
            <button
              type="submit"
              class="btn btn-primary me-2"
              :disabled="loading"
            >
              <i class="bi bi-search me-1"></i>
              Buscar
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary me-2"
              @click="resetFilters"
            >
              <i class="bi bi-x-lg me-1"></i>
              Limpiar
            </button>
            <button
              type="button"
              class="btn btn-outline-primary"
              @click="refreshList"
              :disabled="loading"
            >
              <i class="bi bi-arrow-clockwise me-1"></i>
              Actualizar
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Scripts Table -->
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          Lista de Scripts
          <span v-if="scripts.length" class="badge bg-secondary ms-2">
            {{ scripts.length }}
          </span>
        </h5>
      </div>
      <div class="card-body p-0">
        <!-- Loading state -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="text-muted mt-2">Cargando scripts...</p>
        </div>

        <!-- Empty state -->
        <div v-else-if="scripts.length === 0" class="text-center py-5">
          <i class="bi bi-inbox fs-1 text-muted"></i>
          <p class="text-muted mt-2">No hay scripts configurados</p>
          <router-link to="/scripts/create" class="btn btn-sm btn-primary">
            <i class="bi bi-plus-lg me-1"></i>
            Crear Primer Script
          </router-link>
        </div>

        <!-- Scripts table -->
        <div v-else class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>ID</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>PID</th>
                <th>Configuración</th>
                <th>Iniciado</th>
                <th>Último Heartbeat</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="script in scripts" :key="script.id">
                <td>
                  <span class="badge bg-secondary">{{ script.id }}</span>
                </td>
                <td>
                  <router-link
                    :to="`/clients/${script.client_id}`"
                    class="text-decoration-none"
                  >
                    <i class="bi bi-person-circle me-1"></i>
                    {{ script.client_name }}
                  </router-link>
                  <br>
                  <small class="text-muted">
                    <i class="bi bi-database me-1"></i>
                    {{ script.geotab_database }}
                  </small>
                </td>
                <td>
                  <span :class="getStatusBadgeClass(script.status)">
                    <i :class="getStatusIcon(script.status)" class="me-1"></i>
                    {{ getStatusText(script.status) }}
                  </span>
                </td>
                <td>
                  <span v-if="script.pid" class="badge bg-dark">
                    {{ script.pid }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <div v-if="script.config">
                    <small v-if="script.config.feeder_type">
                      <i class="bi bi-gear me-1"></i>
                      <strong>{{ getFeederTypeName(script.config.feeder_type) }}</strong>
                    </small>
                    <br>
                    <small>
                      <i class="bi bi-clock me-1"></i>
                      Intervalo: {{ script.config.interval || 10 }}s
                    </small>
                    <br v-if="script.config.single_device">
                    <small v-if="script.config.single_device" class="text-muted">
                      <i class="bi bi-hdd me-1"></i>
                      {{ script.config.single_device }}
                    </small>
                  </div>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <small v-if="script.started_at">
                    {{ formatDate(script.started_at) }}
                  </small>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <small v-if="script.last_heartbeat">
                    {{ formatDate(script.last_heartbeat) }}
                  </small>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <div class="btn-group btn-group-sm" role="group">
                    <!-- View logs -->
                    <router-link
                      :to="`/scripts/${script.id}/logs`"
                      class="btn btn-outline-primary"
                      title="Ver logs"
                    >
                      <i class="bi bi-file-text"></i>
                    </router-link>

                    <!-- Edit (only if not running) -->
                    <router-link
                      v-if="script.status !== 'running'"
                      :to="`/scripts/${script.id}/edit`"
                      class="btn btn-outline-secondary"
                      title="Editar"
                    >
                      <i class="bi bi-pencil"></i>
                    </router-link>

                    <!-- Stop (only if running) -->
                    <button
                      v-if="script.status === 'running'"
                      type="button"
                      class="btn btn-outline-warning"
                      @click="stopScript(script.id)"
                      title="Detener"
                    >
                      <i class="bi bi-pause-circle"></i>
                    </button>

                    <!-- Restart -->
                    <button
                      v-if="script.status === 'running' || script.status === 'stopped' || script.status === 'failed'"
                      type="button"
                      class="btn btn-outline-success"
                      @click="restartScript(script.id)"
                      title="Reiniciar"
                    >
                      <i class="bi bi-arrow-clockwise"></i>
                    </button>

                    <!-- Delete (only if not running) -->
                    <button
                      v-if="script.status !== 'running'"
                      type="button"
                      class="btn btn-outline-danger"
                      @click="confirmDelete(script)"
                      title="Eliminar"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useScriptStore } from '../../stores/scripts'
import { useClientStore } from '../../stores/clients'

const scriptStore = useScriptStore()
const clientStore = useClientStore()

// State
const loading = computed(() => scriptStore.loading)
const scripts = computed(() => scriptStore.scripts)
const runningScripts = computed(() => scriptStore.runningScripts)
const stoppedScripts = computed(() => scriptStore.stoppedScripts)
const failedScripts = computed(() => scriptStore.failedScripts)
const totalScripts = computed(() => scriptStore.totalScripts)
const clients = computed(() => clientStore.clients)

const localFilters = ref({
  client_id: null,
  status: null
})

// Methods
const applyFilters = async () => {
  scriptStore.setFilters(localFilters.value)
  await scriptStore.fetchScripts()
}

const resetFilters = async () => {
  localFilters.value = {
    client_id: null,
    status: null
  }
  scriptStore.clearFilters()
  await scriptStore.fetchScripts()
}

const refreshList = async () => {
  await scriptStore.refreshScripts()
}

const stopScript = async (id) => {
  if (confirm('¿Estás seguro de que deseas detener este script?')) {
    try {
      await scriptStore.stopScript(id)
      await scriptStore.refreshScripts()
    } catch (error) {
      console.error('Error stopping script:', error)
    }
  }
}

const restartScript = async (id) => {
  if (confirm('¿Estás seguro de que deseas reiniciar este script?')) {
    try {
      await scriptStore.restartScript(id)
      await scriptStore.refreshScripts()
    } catch (error) {
      console.error('Error restarting script:', error)
    }
  }
}

const confirmDelete = async (script) => {
  if (confirm(`¿Estás seguro de que deseas eliminar el script #${script.id}? Esta acción no se puede deshacer.`)) {
    try {
      await scriptStore.deleteScript(script.id)
    } catch (error) {
      console.error('Error deleting script:', error)
    }
  }
}

const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'badge bg-secondary',
    running: 'badge bg-success',
    stopped: 'badge bg-warning',
    failed: 'badge bg-danger',
    completed: 'badge bg-info'
  }
  return classes[status] || 'badge bg-secondary'
}

const getStatusIcon = (status) => {
  const icons = {
    pending: 'bi-hourglass-split',
    running: 'bi-play-circle-fill',
    stopped: 'bi-pause-circle-fill',
    failed: 'bi-exclamation-circle-fill',
    completed: 'bi-check-circle-fill'
  }
  return icons[status] || 'bi-question-circle'
}

const getStatusText = (status) => {
  const texts = {
    pending: 'Pendiente',
    running: 'En Ejecución',
    stopped: 'Detenido',
    failed: 'Fallido',
    completed: 'Completado'
  }
  return texts[status] || status
}

const getFeederTypeName = (feederType) => {
  const names = {
    localizarte: 'Localizarte',
    pae: 'PAE'
  }
  return names[feederType] || feederType
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    scriptStore.fetchScripts(),
    clientStore.fetchClients(1, 1000) // Get all clients for filter
  ])
})

// Auto-refresh every 10 seconds
const autoRefreshInterval = setInterval(() => {
  scriptStore.refreshScripts()
}, 10000)

// Cleanup on unmount
import { onUnmounted } from 'vue'
onUnmounted(() => {
  clearInterval(autoRefreshInterval)
})
</script>

<style scoped>
.table {
  font-size: 0.9rem;
}

.btn-group-sm > .btn {
  padding: 0.25rem 0.5rem;
}
</style>
